import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShoppingBag,
  Store,
  Bike,
  Loader2,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  Mail,
  MapPin,
  Sparkles,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import BrandLogo from "@/components/BrandLogo";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const roleIcons = { buyer: ShoppingBag, seller: Store, driver: Bike };
const AUTH_HERO = "https://images.unsplash.com/photo-1550547660-d9450f859349?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
const inputClassName = "h-12 rounded-xl border-border bg-[#fcfbfa] px-4 text-sm shadow-none transition-all placeholder:text-muted-foreground/65 focus-visible:border-primary/50 focus-visible:bg-white focus-visible:ring-4 focus-visible:ring-primary/10";

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, googleLogin, register, verifyWhatsApp, changeWhatsAppPhone, user } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register");
  const [role, setRole] = useState(params.get("role") || "buyer");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [pendingPhone, setPendingPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resetIdentifier, setResetIdentifier] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const googleNavigationRef = useRef(false);

  useEffect(() => { if (params.get("role")) setRole(params.get("role")); }, [params]);

  const dest = (r) => (r === "admin" ? "/admin/whatsapp" : r === "seller" ? "/seller" : r === "driver" ? "/driver" : "/app");

  useEffect(() => {
    if (!user || googleNavigationRef.current) return;
    if (user.phone_verified) {
      navigate(dest(user.role), { replace: true });
      return;
    }
    const phone = user.phone || "";
    setPendingPhone(phone);
    setForm((current) => ({ ...current, phone: phone.replace(/^\+963/, "") }));
    setMode(phone ? "verify" : "change-phone");
  }, [user, navigate]);
  useEffect(() => {
    if (!["verify", "reset-password"].includes(mode) || resendSeconds <= 0) return undefined;
    const timer = setInterval(() => setResendSeconds((value) => value - 1), 1000);
    return () => clearInterval(timer);
  }, [mode, resendSeconds]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "verify") {
        await verifyWhatsApp(pendingPhone, otp);
        toast.success("تم تأكيد رقم WhatsApp");
        return;
      }
      if (mode === "forgot-password") {
        const { data } = await api.post("/auth/request-password-reset", { identifier: resetIdentifier.trim() });
        setOtp("");
        setMode("reset-password");
        setResendSeconds(Number(data.resend_after_seconds) || 120);
        toast.success(data.detail);
        return;
      }
      if (mode === "reset-password") {
        if (newPassword.length < 8) {
          toast.error("يجب ألا تقل كلمة المرور عن 8 أحرف.");
          return;
        }
        if (newPassword !== confirmPassword) {
          toast.error("كلمتا المرور غير متطابقتين.");
          return;
        }
        const { data } = await api.post("/auth/confirm-password-reset", {
          identifier: resetIdentifier.trim(),
          otp,
          new_password: newPassword,
        });
        setForm((current) => ({
          ...current,
          email: resetIdentifier.includes("@") ? resetIdentifier.trim() : current.email,
          password: "",
        }));
        setOtp("");
        setNewPassword("");
        setConfirmPassword("");
        setMode("login");
        toast.success(data.detail);
        return;
      }
      if (mode === "change-phone") {
        const result = await changeWhatsAppPhone(`+963${form.phone}`);
        setPendingPhone(result.phone);
        setOtp("");
        setMode("verify");
        setResendSeconds(Number(result.resend_after_seconds) || 120);
        toast.success("تم إرسال رمز التحقق إلى الرقم الجديد");
        return;
      }
      const result = mode === "login"
        ? await login(form.email, form.password)
        : await register({ ...form, phone: `+963${form.phone}`, role });
      if (mode === "register" && result.verification_required) {
        setPendingPhone(result.phone);
        setOtp("");
        setMode("verify");
        setResendSeconds(Number(result.resend_after_seconds) || 120);
        toast.success("تم إنشاء الحساب وإرسال رمز التحقق إلى WhatsApp");
        return;
      }
      if (mode === "login" && result.phone_verified === false) {
        setPendingPhone(result.phone);
        setOtp("");
        setMode("verify");
        try {
          const { data } = await api.post("/auth/request-whatsapp-otp");
          setResendSeconds(Number(data.resend_after_seconds) || 120);
          toast.success("تم إرسال رمز التحقق إلى WhatsApp");
        } catch (err) {
          setResendSeconds(Number(err.response?.data?.retry_after_seconds) || 0);
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
        return;
      }
      toast.success("مرحباً بعودتك!");
      navigate(dest(result.role), { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setLoading(true);
    googleNavigationRef.current = true;
    try {
      const account = await googleLogin(credential, mode === "register" ? role : undefined);
      if (account.phone_verified) {
        toast.success("تم تسجيل الدخول باستخدام Google");
        navigate(dest(account.role), { replace: true });
        return;
      }
      if (!account.phone) {
        toast.success("تم إنشاء حسابك. أضف رقم WhatsApp من ملفك الشخصي قبل تأكيد أي طلب.");
        navigate(dest(account.role), { replace: true });
        return;
      }

      setPendingPhone(account.phone);
      setOtp("");
      setMode("verify");
      try {
        const { data } = await api.post("/auth/request-whatsapp-otp");
        setResendSeconds(Number(data.resend_after_seconds) || 120);
        toast.success("تم إرسال رمز التحقق إلى WhatsApp");
      } catch (error) {
        setResendSeconds(Number(error.response?.data?.retry_after_seconds) || 0);
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
      }
    } catch (error) {
      googleNavigationRef.current = false;
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPhone = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    const phone = value.startsWith("09") ? value.slice(1) : value;
    setForm({ ...form, phone: phone.slice(0, 9) });
    setPhoneError(value && !/^0?9\d{8}$/.test(value) ? "أدخل رقمًا سوريًا يبدأ بـ 9 ويتكوّن من 9 أرقام." : "");
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-whatsapp-otp");
      setResendSeconds(Number(data.resend_after_seconds) || 120);
      toast.success("تم إرسال رمز جديد");
    } catch (err) {
      setResendSeconds(Number(err.response?.data?.retry_after_seconds) || 0);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const resendPasswordReset = async () => {
    try {
      const { data } = await api.post("/auth/request-password-reset", { identifier: resetIdentifier.trim() });
      setResendSeconds(Number(data.resend_after_seconds) || 120);
      toast.success(data.detail);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const title = mode === "login"
    ? "أهلاً بعودتك"
    : mode === "register"
      ? "أنشئ حسابك"
      : mode === "verify"
        ? "تأكيد رقم WhatsApp"
        : mode === "change-phone"
          ? "تعديل رقم الهاتف"
          : mode === "forgot-password"
            ? "استعادة كلمة المرور"
            : "اختر كلمة مرور جديدة";
  const description = mode === "login"
    ? "سجّل دخولك وتابع طلباتك المفضّلة."
    : mode === "register"
      ? "خطوة بسيطة تفصلك عن طلباتك ومتاجرك المفضّلة."
      : mode === "verify"
        ? `أدخل الرمز المرسل إلى ${pendingPhone}.`
        : mode === "change-phone"
          ? "حدّث رقمك لنرسل إليه رمز التحقق."
          : mode === "forgot-password"
            ? "أدخل بريدك أو رقم WhatsApp لإرسال رمز الاستعادة."
            : "أدخل الرمز الذي وصلك، ثم عيّن كلمة مرور جديدة.";

  return (
    <div className="min-h-screen bg-[#f7f4f2]" data-testid="auth-page" dir="rtl">
      <div className="grid min-h-screen lg:grid-cols-2">
        <section className="relative flex min-h-[88px] flex-col overflow-hidden bg-[#32151f] text-white sm:min-h-[290px] lg:order-2 lg:min-h-screen">
          <img
            src={AUTH_HERO}
            alt="وجبة شهية من مطاعم بيب بيب"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#32151f]/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#241018]/90 via-[#32151f]/35 to-[#32151f]/40" />
          <div className="safe-top relative z-10 flex flex-1 flex-col justify-between p-3 sm:p-8 lg:p-12">
            <div className="flex items-start justify-between gap-4">
              <Link to="/" aria-label="بيب بيب - الرئيسية">
                <BrandLogo className="h-9 w-[112px] object-contain sm:h-14 sm:w-[158px]" />
              </Link>
              <div className="lg:hidden">
                <ColorThemeSwitcher variant="mobile" />
              </div>
              <div className="hidden lg:block">
                <ColorThemeSwitcher variant="desktop" />
              </div>
            </div>
            <div className="hidden max-w-xl pb-2 pt-8 sm:block sm:pt-10 lg:pb-8">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 text-xs font-semibold text-white/90 backdrop-blur-sm">
                <Sparkles className="h-4 w-4 text-amber-200" />
                نكهة مدينتك، توصلك أسرع
              </div>
              <h2 className="max-w-lg text-3xl font-extrabold leading-[1.35] tracking-tight sm:text-4xl lg:text-5xl">
                كل ما تحبه من مطاعمك،
                <span className="block text-white/75">على بابك.</span>
              </h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-white/75 sm:text-base">
                اكتشف المتاجر القريبة، اطلب بسهولة، وتابع طلبك حتى يصل إليك.
              </p>
              <div className="mt-5 hidden flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-white/80 sm:flex">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-amber-200" /> متاجر محلية</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-amber-200" /> توصيل لبابك</span>
              </div>
            </div>
          </div>
        </section>

        <main className="relative z-10 -mt-2 flex min-h-[calc(100dvh-5.5rem)] items-start justify-center px-3 pb-8 sm:-mt-9 sm:min-h-[calc(100vh-290px)] sm:px-6 lg:order-1 lg:mt-0 lg:min-h-screen lg:items-center lg:px-8 lg:py-10">
          <section className="w-full max-w-[470px] rounded-[22px] border border-white/80 bg-white p-4 shadow-[0_18px_60px_rgba(55,24,34,0.12)] sm:rounded-[30px] sm:p-8 lg:p-9">
            <div className="mb-5 sm:mb-6">
              <div className="mb-3 inline-flex items-center gap-1.5 text-xs font-bold text-primary">
                <KeyRound className="h-3.5 w-3.5" />
                حساب بيب بيب
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight text-[#302329] sm:text-[30px]">{title}</h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
            </div>

            {mode === "register" && (
              <div className="mb-5 grid grid-cols-3 gap-2">
                {Object.keys(roleIcons).map((r) => {
                  const Icon = roleIcons[r];
                  const active = role === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      data-testid={`select-role-${r}`}
                      onClick={() => setRole(r)}
                      aria-pressed={active}
                      className={`min-h-[76px] rounded-xl border px-2 py-3 text-center transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15 ${
                        active
                          ? "border-primary/35 bg-primary/[0.06] text-primary shadow-sm"
                          : "border-border bg-[#fcfbfa] text-muted-foreground hover:border-primary/25 hover:bg-primary/[0.025]"
                      }`}
                    >
                      <Icon className="mx-auto h-5 w-5" />
                      <div className="mt-1.5 text-[11px] font-bold sm:text-xs">{ROLE_LABELS[r]}</div>
                    </button>
                  );
                })}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              {mode === "verify" ? (
                <>
                  <div className="rounded-xl border border-primary/10 bg-primary/[0.045] p-3.5 text-sm leading-6 text-foreground/80">
                    أدخل رمز التحقق المرسل إلى WhatsApp لإكمال تأكيد رقمك.
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="input-otp">رمز التحقق</Label>
                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input id="input-otp" data-testid="input-otp" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" dir="ltr" className={`${inputClassName} pr-11 text-center tracking-[0.5em]`} />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                    <button type="button" disabled={resendSeconds > 0} className="font-bold text-primary transition-colors hover:text-primary/75 disabled:cursor-not-allowed disabled:text-muted-foreground" onClick={resend}>
                      {resendSeconds > 0 ? `إعادة الإرسال بعد ${resendSeconds} ث` : "إعادة إرسال الرمز"}
                    </button>
                    <button type="button" className="font-semibold text-muted-foreground transition-colors hover:text-primary" onClick={() => setMode("change-phone")}>تعديل رقم الهاتف</button>
                  </div>
                </>
              ) : mode === "reset-password" ? (
                <>
                  <div className="rounded-xl border border-primary/10 bg-primary/[0.045] p-3.5 text-sm leading-6 text-foreground/80">
                    إذا كانت البيانات مرتبطة بحساب، سيصلك رمز الاستعادة على WhatsApp المسجل.
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="input-otp">رمز الاستعادة</Label>
                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input id="input-otp" data-testid="input-otp" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" dir="ltr" className={`${inputClassName} pr-11 text-center tracking-[0.5em]`} />
                    </div>
                    <button type="button" disabled={resendSeconds > 0} className="text-sm font-bold text-primary transition-colors hover:text-primary/75 disabled:cursor-not-allowed disabled:text-muted-foreground" onClick={resendPasswordReset}>
                      {resendSeconds > 0 ? `إعادة الإرسال بعد ${resendSeconds} ث` : "إعادة إرسال الرمز"}
                    </button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="input-new-password">كلمة المرور الجديدة</Label>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input id="input-new-password" data-testid="input-new-password" type={showNewPassword ? "text" : "password"} minLength={8} autoComplete="new-password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={`${inputClassName} pl-12 pr-11`} />
                      <button type="button" aria-label={showNewPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"} onClick={() => setShowNewPassword((value) => !value)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="input-confirm-password">تأكيد كلمة المرور</Label>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input id="input-confirm-password" data-testid="input-confirm-password" type={showConfirmPassword ? "text" : "password"} minLength={8} autoComplete="new-password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={`${inputClassName} pl-12 pr-11`} />
                      <button type="button" aria-label={showConfirmPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"} onClick={() => setShowConfirmPassword((value) => !value)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <button type="button" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-primary" onClick={() => setMode("forgot-password")}>تعديل البريد أو رقم الهاتف</button>
                </>
              ) : mode === "forgot-password" ? (
                <div className="space-y-2">
                  <Label htmlFor="input-reset-identifier">البريد الإلكتروني أو رقم WhatsApp</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input id="input-reset-identifier" data-testid="input-reset-identifier" required value={resetIdentifier} onChange={(e) => setResetIdentifier(e.target.value)} placeholder="you@example.com أو 09xxxxxxxx" dir="ltr" className={`${inputClassName} pr-11 text-start`} />
                  </div>
                </div>
              ) : mode === "change-phone" ? (
                <>
                  <div className="rounded-xl border border-primary/10 bg-primary/[0.045] p-3.5 text-sm text-foreground/80">
                    سيبقى رمز الدولة سوريا <span className="font-bold" dir="ltr">+963</span>.
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="input-phone">رقم الهاتف الجديد</Label>
                    <div className="flex gap-2" dir="ltr">
                      <span className="inline-flex h-12 items-center rounded-xl border border-border bg-[#f5f1ef] px-3 text-sm font-bold text-foreground/70" aria-label="رمز الدولة الثابت">+963</span>
                      <Input id="input-phone" data-testid="input-phone" required inputMode="numeric" maxLength={10} value={form.phone} onChange={setPhone} placeholder="9XXXXXXXX" dir="ltr" className={`${inputClassName} text-start`} />
                    </div>
                    {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
                  </div>
                  <button type="button" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-primary" onClick={() => setMode("verify")}>إلغاء</button>
                </>
              ) : (
                <>
                  {mode === "register" && (
                    <div className="space-y-2">
                      <Label htmlFor="input-name">الاسم الكامل</Label>
                      <div className="relative">
                        <UserRound className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input id="input-name" data-testid="input-name" required value={form.name} onChange={set("name")} placeholder="مثال: أحمد العلي" className={`${inputClassName} pr-11`} />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="input-email">البريد الإلكتروني</Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input id="input-email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" dir="ltr" className={`${inputClassName} pr-11 text-start`} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="input-password">كلمة المرور</Label>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input id="input-password" data-testid="input-password" type={showPassword ? "text" : "password"} required value={form.password} onChange={set("password")} placeholder="••••••••" autoComplete={mode === "login" ? "current-password" : "new-password"} className={`${inputClassName} pl-12 pr-11`} />
                      <button type="button" aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"} onClick={() => setShowPassword((value) => !value)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {mode === "login" && (
                    <div className="flex justify-start">
                      <button type="button" className="text-sm font-bold text-primary transition-colors hover:text-primary/75" onClick={() => { setResetIdentifier(form.email); setMode("forgot-password"); }}>
                        نسيت كلمة المرور؟
                      </button>
                    </div>
                  )}
                  {mode === "register" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="input-phone">رقم الهاتف</Label>
                        <div className="flex gap-2" dir="ltr">
                          <span className="inline-flex h-12 items-center rounded-xl border border-border bg-[#f5f1ef] px-3 text-sm font-bold text-foreground/70" aria-label="رمز الدولة الثابت">+963</span>
                          <Input id="input-phone" data-testid="input-phone" required inputMode="numeric" maxLength={10} value={form.phone} onChange={setPhone} placeholder="9XXXXXXXX" dir="ltr" className={`${inputClassName} text-start`} />
                        </div>
                        {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
                      </div>
                      {role === "buyer" && (
                        <div className="space-y-2">
                          <Label htmlFor="input-address">العنوان (الحي)</Label>
                          <div className="relative">
                            <MapPin className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input id="input-address" data-testid="input-address" value={form.address} onChange={set("address")} placeholder="مثال: حي الأمل" className={`${inputClassName} pr-11`} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="mt-2 h-12 w-full rounded-xl bg-primary text-base font-bold shadow-[0_8px_20px_rgba(123,40,76,0.2)] transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-[0_12px_26px_rgba(123,40,76,0.25)]">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (mode === "login" ? "تسجيل الدخول" : mode === "verify" ? "تأكيد الرقم" : mode === "change-phone" ? "إرسال رمز التحقق" : mode === "forgot-password" ? "إرسال رمز WhatsApp" : mode === "reset-password" ? "تغيير كلمة المرور" : "إنشاء الحساب")}
                {!loading && <ArrowLeft className="mr-1 h-4 w-4" />}
              </Button>
            </form>

            {["login", "register"].includes(mode) && (
              <GoogleSignInButton onCredential={handleGoogleCredential} disabled={loading} />
            )}

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {["login", "register"].includes(mode) && <>{mode === "login" ? "ليس لديك حساب؟" : "لديك حساب بالفعل؟"}{" "}
                <button data-testid="toggle-auth-mode" type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-extrabold text-primary transition-colors hover:text-primary/75">
                  {mode === "login" ? "أنشئ حساباً" : "سجّل الدخول"}
                </button></>}
              {["forgot-password", "reset-password"].includes(mode) && (
                <button type="button" onClick={() => setMode("login")} className="inline-flex items-center gap-1 font-bold text-primary transition-colors hover:text-primary/75"><ArrowRight className="h-4 w-4" /> العودة إلى تسجيل الدخول</button>
              )}
            </p>
            <p className="mt-5 border-t border-border/70 pt-4 text-center text-[11px] text-muted-foreground/80">
              © 2026 بيب بيب — طلباتك المفضّلة أقرب إليك
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}
